/**
 * Interface copy, Uzbek. The primary UI language (decision D2).
 *
 * Rules this file follows (§10.8):
 *  - Name things by what the user controls, never by how the system is built.
 *    No "slug", no "meta", no "component", no "entity", no "locale".
 *  - Buttons say what happens. The same verb survives into the confirmation
 *    and the toast: `Nashr etish` → `Nashr etildi`.
 *  - Sentence case everywhere. No Title Case, no ALL CAPS outside the eyebrow.
 *  - Errors state the problem and the fix.
 *  - Empty screens invite action; they do not explain absence.
 */
export const uz = {
  brand: { name: 'iPost', suffix: 'boshqaruv paneli' },

  nav: {
    overview: 'Boshqaruv paneli',
    articles: 'Yangiliklar',
    services: 'Xizmatlar',
    stories: 'Hikoyalar',
    pages: 'Sahifalar',
    media: 'Rasmlar',
    categories: 'Toifalar',
    redirects: "Yo'naltirishlar",
    leads: 'Murojaatlar',
    scans: 'RS skanerlar',
    settings: 'Sozlamalar',
    users: 'Foydalanuvchilar',
    audit: "O'zgarishlar tarixi",
    groupApp: 'Ilova',
    groupContent: 'Kontent',
    groupManage: 'Boshqaruv',
    groupSystem: 'Tizim',
  },

  common: {
    save: 'Saqlash',
    saving: 'Saqlanmoqda…',
    saved: 'Saqlandi',
    cancel: 'Bekor qilish',
    close: 'Yopish',
    delete: "O'chirish",
    deleted: "O'chirildi",
    restore: 'Qaytarish',
    edit: 'Tahrirlash',
    create: "Qo'shish",
    search: 'Qidirish',
    filter: 'Saralash',
    all: 'Hammasi',
    none: "Yo'q",
    yes: 'Ha',
    no: "Yo'q",
    back: 'Orqaga',
    next: 'Keyingi',
    prev: 'Oldingi',
    loading: 'Yuklanmoqda',
    retry: 'Qayta urinish',
    copy: 'Nusxalash',
    copied: 'Nusxalandi',
    undo: 'Qaytarish',
    optional: 'ixtiyoriy',
    required: 'majburiy',
    moreActions: 'Boshqa amallar',
    collapseSidebar: 'Menyuni yig‘ish',
    expandSidebar: 'Menyuni ochish',
    openMenu: 'Menyu',
    unknown: "noma'lum",
    of: 'dan',
    openOnSite: 'Saytda ochish',
    details: 'Batafsil',
  },

  status: {
    draft: 'Qoralama',
    published: 'Nashr etilgan',
    live: 'Saytda',
    liveOn: 'Saytda ko‘rinmoqda',
    notLive: 'Hali saytda emas',
  },

  auth: {
    signInTitle: 'Boshqaruv paneli',
    signInSubtitle: 'Kontent va SEO boshqaruvi',
    email: 'Email',
    login: 'Login',
    password: 'Parol',
    showPassword: "Parolni ko'rsatish",
    hidePassword: 'Parolni yashirish',
    signIn: 'Kirish',
    signingIn: 'Kirilmoqda…',
    signOut: 'Chiqish',
    changePasswordTitle: "Parolni o'zgartiring",
    changePasswordWhy:
      "Hisobingizga vaqtinchalik parol berilgan. Davom etishdan oldin o'zingiznikini o'ylab toping.",
    currentPassword: 'Joriy parol',
    newPassword: 'Yangi parol',
    repeatPassword: 'Yangi parolni takrorlang',
    passwordsDiffer: 'Parollar bir xil emas',
    changePassword: "Parolni o'zgartirish",
    passwordChanged: "Parol o'zgartirildi",
  },

  overview: {
    title: 'Boshqaruv paneli',
    greetingMorning: 'Xayrli tong',
    greetingDay: 'Xayrli kun',
    greetingEvening: 'Xayrli kech',
    attention: 'Diqqat talab qiladi',
    nothingToDo: 'Hozircha hammasi joyida',
    nothingToDoHint: "Qoralamalar yo'q, barcha nashrlar to'liq.",
    drafts: 'tugallanmagan qoralama',
    missingRu: 'ruscha matnsiz yangilik',
    missingEn: 'inglizcha tarjimasiz yangilik',
    missingSeo: "Google uchun matni yo'q nashr",
    noCover: 'muqova rasmisiz nashr',
    unreadLeads: "o'qilmagan murojaat",
    recent: "So'nggi o'zgarishlar",
    noRecent: "Hozircha o'zgarishlar yo'q",
    quickActions: 'Tez amallar',
    newArticle: "Yangilik qo'shish",
    openMedia: 'Rasmlar kutubxonasi',
    system: 'Tizim holati',
    storage: 'Fayl xotirasi',
    storageOk: 'ishlayapti',
    storageDown: 'ulanmadi',
    mediaCount: 'Rasmlar',
    mediaUnused: 'Ishlatilmayotgan rasmlar',
    published: 'Saytdagi nashrlar',
  },

  articles: {
    title: 'Yangiliklar',
    subtitle: 'Saytdagi yangiliklar bo‘limi',
    new: "Yangilik qo'shish",
    emptyTitle: "Hali birorta yangilik yo'q",
    emptyHint: 'Birinchi yangilikni qo‘shing — u darhol saytda ko‘rinadi.',
    emptyAction: "Birinchi yangilikni qo'shing",
    noResultsTitle: 'Hech narsa topilmadi',
    noResultsHint: 'Boshqa so‘z bilan qidiring yoki saralashni o‘chiring.',
    columnTitle: 'Sarlavha',
    columnLanguages: 'Tillar',
    columnCategory: 'Toifa',
    columnUpdated: "O'zgartirilgan",
    columnStatus: 'Holati',
    untitled: 'Nomsiz yangilik',
    deleteConfirm: 'Bu yangilikni o‘chirasizmi?',
    deleteConfirmBody:
      'Yangilik saytdan olib tashlanadi. Keyin uni qaytarish mumkin.',
    filterAll: 'Hammasi',
    filterDrafts: 'Qoralamalar',
    filterPublished: 'Nashr etilganlar',
    filterMissingRu: 'Ruschasi yo‘q',
    filterMissingSeo: 'SEO to‘ldirilmagan',
    // Uzbek does not inflect the noun after a numeral — "4 yangiliklar" is
    // wrong. The counter is a function so each language can say it correctly.
    total: (n: number) => `${n} ta yangilik`,
  },

  editor: {
    backToList: 'Yangiliklar',
    newTitle: 'Yangi yangilik',
    contentGroup: 'Matn',
    addressGroup: 'Sahifa manzili',
    searchGroup: "Google'da qanday ko'rinadi",
    socialGroup: 'Ijtimoiy tarmoqlarda ulashilganda',
    publishingGroup: 'Nashr',
    advancedGroup: 'Qidiruv tizimi sozlamalari',

    titleLabel: 'Sarlavha',
    titlePlaceholder: 'Nima haqida?',
    bodyLabel: 'Matn',
    bodyPlaceholder: 'Shu yerdan yozishni boshlang…',

    slugLabel: 'Sahifa manzili',
    slugHint: 'Bu manzil brauzerda va Google‘da ko‘rinadi.',
    slugFromTitle: 'Sarlavhadan olish',
    slugRenameWarning: 'Eski manzil yangisiga yo‘naltiriladi:',
    slugTaken: 'Bu manzil boshqa yangilikda ishlatilyapti. Boshqasini tanlang.',
    slugRequired: 'Avval sarlavha yoki sahifa manzilini kiriting.',

    metaTitleLabel: "Google'da ko'rinadigan sarlavha",
    metaTitleHint: 'Bo‘sh qoldirilsa, yangilik sarlavhasi ishlatiladi.',
    metaDescLabel: "Google'dagi qisqa tavsif",
    metaDescHint: 'Odam natijalar ro‘yxatida shuni o‘qiydi.',
    metaKeywordsLabel: 'Kalit so‘zlar',
    metaKeywordsHint: 'Vergul bilan ajrating. 10 tagacha.',
    metaKeywordsPlaceholder: 'xitoydan yuk tashish, aviayetkazish, bojxona',
    metaTooLong: 'Google buni qisqartirib ko‘rsatadi',
    metaGood: 'Uzunligi yaxshi',
    metaEmpty: 'To‘ldirilmagan',

    coverLabel: 'Muqova rasmi',
    coverEmpty: 'Rasm tanlang',
    coverChange: 'Almashtirish',
    coverRemove: 'Olib tashlash',
    altLabel: 'Rasm tavsifi',
    altHint: 'Ko‘rmaydigan odamlar va Google shu matnni o‘qiydi.',
    altMissing: 'Rasm tavsifi yozilmagan',

    ogLabel: 'Ulashish rasmi',
    ogHint: 'Telegram va ijtimoiy tarmoqlarda shu rasm chiqadi. Bo‘sh bo‘lsa muqova ishlatiladi.',

    categoryLabel: 'Toifa',
    categoryNone: 'Tanlanmagan',
    publishedAtLabel: 'Nashr vaqti',
    canonicalLabel: 'Asosiy manzil',
    canonicalHint:
      'Bu matn boshqa saytda ham chiqsa, asl manzilni yozing. Odatda bo‘sh qoldiriladi.',
    noIndexLabel: "Google'ga qo'shilmasin",
    noIndexHint: 'Sahifa saytda qoladi, lekin qidiruvda chiqmaydi.',

    preview: "Ko'rish",
    previewHint: 'Nashr etmasdan turib, saytda qanday chiqishini ko‘rish',
    previewOpening: 'Ochilmoqda…',
    publish: 'Nashr etish',
    publishing: 'Nashr etilmoqda…',
    published: 'Nashr etildi',
    saveOk: 'Muvaffaqiyatli saqlandi',
    unpublish: 'Saytdan olish',
    unpublishHint: 'Sahifa saytdan olinadi va yana qoralamaga aylanadi',
    unpublished: 'Saytdan olindi',
    lastSaved: 'oxirgi saqlash',
    neverSaved: 'hali saqlanmagan',
    unsaved: 'saqlanmagan o‘zgarishlar bor',

    readyTitle: 'Nashr etishga tayyor',
    readyAllGood: 'Hammasi joyida',
    blockersTitle: 'Nashr etish uchun quyidagilar kerak',
    warningsTitle: 'Diqqat qiling',

    conflictTitle: 'Bu yangilikni boshqa foydalanuvchi o‘zgartirdi',
    conflictBody: 'o‘zgartirdi. Qaysi variantni saqlaymiz?',
    conflictKeepMine: 'Meniki qolsin',
    conflictTakeTheirs: 'Ularniki qolsin',
    conflictOpenNew: 'Yangi oynada ochish',

    revalidateFailed:
      'Yangilik saytda, lekin sahifa keshi yangilanmadi. Bir necha daqiqadan keyin o‘zi yangilanadi.',
    revalidateRetry: 'Qayta urinish',

    // Every toolbar button gets a name. An icon alone is a guess.
    tools: {
      bold: 'Qalin',
      italic: 'Qiya',
      h2: 'Sarlavha',
      h3: 'Kichik sarlavha',
      bullets: 'Belgili ro‘yxat',
      numbers: 'Raqamli ro‘yxat',
      quote: 'Iqtibos',
      link: 'Havola qo‘shish',
      image: 'Rasm qo‘yish',
      video: 'Video qo‘yish',
    },
    linkTitle: 'Havola',
    linkHint: 'Ichki manzil (/uz/services) yoki to‘liq manzil (https://…)',
    linkAddress: 'Manzil',
    linkRemove: 'Havolani olib tashlash',
    videoTitle: 'Video qo‘yish',
    videoHint: 'YouTube, Vimeo yoki Instagram havolasini joylang — saytda video bo‘lib chiqadi.',
    videoAddress: 'Video havolasi',
    videoInvalid: 'Havola noto‘g‘ri. YouTube/Vimeo/Instagram yoki to‘liq https havola bo‘lsin.',
    wordCount: (n: number) => `${n} ta so‘z`,
    readingTime: (n: number) => `~${n} daqiqa o‘qish`,
  },

  /**
   * Field explanations. Every one answers three questions in this order:
   * what is it, how is it written, and what does a good one look like.
   *
   * They exist because "Meta Title" is a database word. A marketer should not
   * have to know it — they should read one sentence and recognise the job.
   */
  help: {
    slug: {
      title: 'Sahifa manzili nima?',
      what: 'Bu — sahifaning brauzer manzil qatorida va Google natijalarida ko‘rinadigan qismi.',
      how: 'Faqat kichik lotin harflari, raqamlar va tire. Bo‘sh joy, kirill va belgilar ishlatilmaydi.',
      exampleLabel: 'Masalan',
      exampleFrom: "Xitoydan yangi yo'nalish ochildi",
      exampleTo: 'xitoydan-yangi-yonalish',
      tips: [
        '3–6 ta so‘z yetarli. Uzun manzil qidiruvda kesiladi.',
        'Asosiy so‘zni ichiga qo‘ying — «yonalish», «aviayetkazish».',
        'Sana va raqam qo‘shmang: 2026 keyingi yil eskiradi.',
      ],
      warn: 'Nashr etilgan sahifa manzilini o‘zgartirsangiz, eski manzil avtomatik ravishda yangisiga yo‘naltiriladi. Eski havolalar ishlashda davom etadi.',
    },
    metaTitle: {
      title: "Google'da ko'rinadigan sarlavha",
      what: 'Google natijalarida ko‘k rangda chiqadigan, bosiladigan sarlavha. Brauzer oynasida ham shu ko‘rinadi.',
      how: '50–60 belgi eng yaxshisi. 70 dan oshsa, Google uni «…» bilan kesadi.',
      exampleLabel: 'Yaxshi misol',
      exampleFrom: 'Yangi yo‘nalish haqida yangilik',
      exampleTo: "Xitoydan Toshkentga to'g'ridan-to'g'ri yuk tashish — iPost",
      tips: [
        'Eng muhim so‘zni boshiga qo‘ying — odam birinchi so‘zlarni o‘qiydi.',
        'Oxiriga «— iPost» qo‘shing, brend tanilishi uchun.',
        'Har bir sahifada boshqacha bo‘lsin. Takrorlangan sarlavha Google‘ni chalg‘itadi.',
      ],
      warn: 'Bo‘sh qoldirilsa, yangilik sarlavhasi ishlatiladi. Bu ham ishlaydi, lekin alohida yozilgani deyarli har doim yaxshiroq.',
    },
    metaDesc: {
      title: "Google'dagi qisqa tavsif",
      what: 'Sarlavha ostidagi ikki qatorli kulrang matn. Odam bosishdan oldin aynan shuni o‘qiydi.',
      how: '140–160 belgi. 180 dan oshmaydi.',
      exampleLabel: 'Yaxshi misol',
      exampleFrom: 'Bu yangilikda yangi yo‘nalish haqida yozilgan.',
      exampleTo: 'Xitoydan Toshkentga to‘g‘ridan-to‘g‘ri yo‘nalish ishga tushdi. Yetkazish 4 kunga qisqardi. Narxni hisoblang.',
      tips: [
        'Nima taklif qilayotganingizni va nima qilish kerakligini yozing.',
        'Sarlavhani takrorlamang — bu qo‘shimcha joy, uni behuda sarflamang.',
        'Raqam va muddat ishonch beradi: «4 kun», «12–18 kun».',
      ],
      warn: 'Bu matn Google reytingiga bevosita ta’sir qilmaydi, lekin bosish sonini sezilarli oshiradi.',
    },
    /**
     * This one tells the truth. The field exists because it was asked for; a
     * marketer who spends an hour a week filling it in deserves to know that
     * the hour buys nothing, so the effort goes into the description instead.
     */
    metaKeywords: {
      title: 'Kalit so‘zlar — rostini aytganda',
      what: 'Bu «keywords» meta tegi. Google 2009-yildan beri uni umuman o‘qimaydi, Yandex ham reyting uchun ishlatmaydi.',
      how: 'Vergul bilan ajratib yozing. 10 tadan oshmasin — ko‘p bo‘lsa spam sifatida qabul qilinadi.',
      exampleLabel: 'Agar to‘ldirsangiz',
      exampleFrom: 'yuk, tashish, xitoy, avia, tez, arzon, eng yaxshi, narx, 2026',
      exampleTo: 'xitoydan yuk tashish, aviayetkazish, bojxona rasmiylashtiruvi',
      tips: [
        'Bu maydonga sarflagan vaqtingizni «Google‘dagi qisqa tavsif»ga sarflang — o‘sha haqiqatan ham ta’sir qiladi.',
        'Kalit so‘zlar sarlavha va matn ichida bo‘lsa, foydasi bor. Bu tegda emas.',
        'Bo‘sh qoldirsangiz, hech narsa yo‘qotmaysiz.',
      ],
      warn: 'Maydon sizning so‘rovingiz bo‘yicha qo‘shildi va saytga chiqariladi. Lekin qidiruv natijalariga ta’siri yo‘q — buni bilib turishingiz uchun shu yerda yozib qo‘ydik.',
    },
    languages: {
      title: 'Tillar qanday ishlaydi',
      what: 'Har bir til alohida to‘ldiriladi: sarlavha, matn va Google matni har biri uchun mustaqil saqlanadi.',
      how: 'O‘zbekcha va ruscha — majburiy. Inglizcha — ixtiyoriy.',
      exampleLabel: 'To‘rtta nuqta',
      exampleFrom: 'sarlavha · matn · Google matni · rasm',
      exampleTo: 'to‘lgan nuqta — yozilgan, bo‘sh nuqta — hali yo‘q',
      tips: [
        'Tilni almashtirish uchun yuqoridagi katakchani bosing.',
        'Uzuq chiziqli katak — bu til hali yozilmagan. Bu xato emas.',
        'Inglizchasi bo‘lmasa, saytda ruscha matn ko‘rsatiladi.',
      ],
      warn: 'Inglizchasi yo‘q sahifa Google‘ga qo‘shilmaydi — bir xil matn ikki manzilda turishining oldini olish uchun shunday qilingan.',
    },
    cover: {
      title: 'Muqova rasmi',
      what: 'Yangiliklar ro‘yxatida va sahifa yuqorisida ko‘rinadigan rasm.',
      how: 'Eni bo‘yiga nisbatan 16:9. Kamida 1200 piksel kenglikda. 10 MB gacha.',
      exampleLabel: 'Tavsiya',
      exampleFrom: 'Ekran surati, matnli banner',
      exampleTo: 'Haqiqiy fotosurat: konteyner, ombor, samolyot',
      tips: [
        'Rasm tavsifini («alt») to‘ldiring — Google shuni o‘qiydi.',
        'Rasm ustiga matn yozmang: telefonda o‘qilmaydi.',
      ],
      warn: '',
    },
    canonical: {
      title: 'Asosiy manzil',
      what: 'Agar shu matn boshqa saytda ham chop etilgan bo‘lsa, asl manba manzilini shu yerga yozing.',
      how: 'To‘liq manzil, https:// bilan.',
      exampleLabel: 'Masalan',
      exampleFrom: '/news/xitoydan',
      exampleTo: 'https://ipost.uz/uz/news/xitoydan-yangi-yonalish',
      tips: ['Deyarli har doim bo‘sh qoldiriladi. Ishonchingiz komil bo‘lmasa — tegmang.'],
      warn: '',
    },
    noIndex: {
      title: "Google'ga qo'shilmasin",
      what: 'Sahifa saytda qoladi va havola orqali ochiladi, lekin Google qidiruvida chiqmaydi.',
      how: 'Vaqtinchalik yoki ichki sahifalar uchun.',
      exampleLabel: 'Qachon kerak',
      exampleFrom: 'Oddiy yangilik',
      exampleTo: 'Faqat mijozga yuboriladigan aksiya sahifasi',
      tips: ['Odatdagi yangilik uchun bu kerak emas.'],
      warn: '',
    },
    close: 'Tushunarli',
  },

  stamp: {
    complete: "to'liq",
    seoLeft: 'SEO qolgan',
    empty: "yozilmagan",
    switchTo: 'ushbu tilga o‘tish',
    dotTitle: 'sarlavha',
    dotBody: 'matn',
    dotSeo: 'Google matni',
    dotCover: 'rasm',
  },

  services: {
    title: 'Xizmatlar',
    subtitle: 'Har bir xizmat uchun alohida sahifa',
    new: "Xizmat qo'shish",
    emptyTitle: "Hali xizmat sahifalari yo'q",
    emptyHint: 'Har bir xizmat uchun alohida sahifa yarating — ular saytda ro‘yxat bo‘lib chiqadi.',
    emptyAction: "Birinchi xizmatni qo'shing",
    excerptLabel: 'Qisqa tavsif',
    excerptHint: 'Xizmatlar ro‘yxatida shu matn ko‘rinadi.',
    iconLabel: 'Belgi',
    orderHint: 'Tartibni sudrab o‘zgartiring.',
    deleteConfirm: 'Bu xizmatni o‘chirasizmi?',
    deleteConfirmBody: 'Xizmat saytdan yo‘qoladi. Uni keyin qaytarib bo‘ladi.',
    slugRequired: 'Avval sarlavha yoki sahifa manzilini kiriting.',
  },

  stories: {
    title: 'Hikoyalar',
    subtitle: 'Mobil ilova uchun aylana hikoyalar',
    new: 'Yangi hikoya',
    emptyTitle: "Hali hikoya yo'q",
    emptyHint: 'Birinchi hikoyani yarating — u ilovada aylana ko‘rinishida chiqadi.',
    emptyAction: 'Birinchi hikoyani yarating',

    titleLabel: 'Sarlavha',
    titleHint: 'Hikoya aylanasi ostida ko‘rinadigan nom.',
    coverLabel: 'Muqova',
    coverHint: 'Aylana ichida ko‘rinadigan rasm. Bo‘sh bo‘lsa birinchi slayd ishlatiladi.',
    ttlLabel: 'Davomiyligi',
    ttlHint: 'Chop etilgach hikoya shuncha vaqtdan so‘ng ilovadan o‘chadi.',
    ttlOption: (n: number) => (n % 24 === 0 ? `${n} soat (${n / 24} kun)` : `${n} soat`),
    orderLabel: 'Tartib',
    orderHint: 'Kichik raqam oldinroq turadi.',

    slidesGroup: 'Slaydlar',
    slidesHint: 'Ko‘ruvchi ketma-ket varaqlaydi.',
    addSlide: 'Slayd qo‘shish',
    removeSlide: 'Slaydni o‘chirish',
    moveUp: 'Yuqoriga',
    moveDown: 'Pastga',
    slideN: (n: number) => `${n}-slayd`,
    noSlides: 'Hali slayd yo‘q. Kamida bittasini qo‘shing.',

    mediaTypeImage: 'Rasm',
    mediaTypeVideo: 'Video',
    mediaLabel: 'Media',
    mediaEmpty: 'Media tanlang',
    mediaChange: 'Almashtirish',
    posterLabel: 'Birinchi kadr',
    posterHint: 'Video yuklanguncha ko‘rinadigan rasm.',
    posterEmpty: 'Rasm tanlang',
    captionLabel: 'Izoh',
    buttonGroup: 'Tugma',
    buttonHint: 'Slayd pastida tugma bo‘lib chiqadi',
    buttonOn: 'Yoqilgan',
    buttonOff: 'O‘chirilgan',
    buttonNeedsUrl: 'Tugma ishlashi uchun havola kiriting',
    linkUrlLabel: 'Tugma havolasi',
    linkUrlHint: 'To‘liq manzil (https://…) yoki ilova ichidagi yo‘l.',
    linkLabelLabel: 'Tugma matni',
    durationLabel: 'Davomiyligi',
    durationSuffix: 'soniya',

    views: 'Ko‘rishlar',
    expired: "Muddati o'tgan",
    slideCount: (n: number) => `${n} ta slayd`,
    hours: (n: number) => `${n} soat`,

    deleteConfirm: 'Bu hikoyani o‘chirasizmi?',
    deleteConfirmBody: 'Hikoya ilovadan yo‘qoladi. Uni keyin qaytarib bo‘ladi.',

    blockerNoSlides: 'Kamida bitta slayd qo‘shing',
    blockerSlideMedia: (n: number) => `${n}-slayd uchun media tanlanmagan`,

    titlePlaceholder: 'Masalan: Yangi avia reys',
    publishBlocked: 'Chop etish uchun kamida bitta media-li slayd kerak',
    previewLabel: 'Jonli ko‘rinish',
    previewNow: 'hozir',
    previewEmpty: 'Media tanlang',
    previewHint: 'Ilovada aynan shunday ko‘rinadi. Slaydni tanlab ko‘rib chiqing.',

    // Tray filtri.
    filterAll: 'Barchasi',
    filterPublished: 'Chop etilgan',
    filterUnpublished: 'Chop etilmagan',
    filterEmpty: 'Bu holatda hikoya yo‘q',

    // Ilova (sayt emas) uchun holat/amal so‘zlari.
    statusLive: 'Ilovada',
    statusDraft: 'Qoralama',
    publish: 'Chop etish',
    unpublish: 'Ilovadan olish',
    unpublishHint: 'Hikoya ilovadan vaqtincha olinadi. Keyin qayta chop etsa bo‘ladi.',
    published: 'Hikoya ilovada chop etildi',

    uploadMedia: 'Rasm/video yuklang',
    captionHint: 'Slayd ustida chiqadigan qisqa matn (ixtiyoriy).',
    captionPlaceholder: 'Masalan: Shenzhen–Toshkent 3 kunda',
    linkLabelPlaceholder: 'Masalan: Batafsil',
  },

  pages: {
    title: 'Sahifalar',
    subtitle: 'Saytdagi doimiy sahifalarning Google matnlari',
    empty: 'To‘ldirilmagan',
    filled: 'To‘ldirilgan',
    partial: 'Qisman',
    groupMain: 'Asosiy sahifalar',
    groupMainHint: 'Saytning eng muhim sahifalari — avvalo shularni to‘ldiring',
    groupMore: 'Qo‘shimcha sahifalar',
    groupMoreHint: 'Yordamchi va huquqiy sahifalar',
    summaryTitle: 'SEO to‘ldirilishi',
    summary: (filled: number, total: number) => `${total} sahifadan ${filled} tasi to‘liq to‘ldirilgan`,
    openHint: 'Ochish uchun bosing',
    headingLabel: 'Sahifa sarlavhasi',
    seoTextLabel: 'SEO matni',
    seoTextHint:
      'Bu matn sahifa oxirida, footerdan oldin chiqadi. Sarlavhalar, ro‘yxatlar va havolalar ishlatish mumkin.',
    routeNames: {
      '/': 'Bosh sahifa',
      '/about': 'Biz haqimizda',
      '/services': 'Xizmatlar',
      '/news': 'Yangiliklar',
      '/contact': 'Kontaktlar',
      '/offices': 'Ofislar',
      '/track': 'Kuzatuv',
      '/careers': 'Ish o‘rinlari',
      '/media': 'Matbuot',
      '/privacy': 'Maxfiylik siyosati',
      '/terms': 'Foydalanish shartlari',
    } as Record<string, string>,
  },

  categories: {
    title: 'Toifalar',
    subtitle: 'Har bir toifaning saytda alohida sahifasi bor',
    new: "Toifa qo'shish",
    nameLabel: 'Toifa nomi',
    keyLabel: 'Tizim nomi',
    keyHint: 'O‘zgarmaydi. Sayt shu nom bo‘yicha yangiliklarni ajratadi.',
    articleCount: 'yangilik',
    countLabel: (n: number) => `${n} ta yangilik`,
    emptyTitle: "Toifalar yo'q",
    emptyHint: 'Toifalar yangiliklarni guruhlaydi va har biri alohida sahifaga ega bo‘ladi.',
    emptyAction: "Birinchi toifani qo'shing",
    deleteHasArticles: 'Bu toifada yangiliklar bor. Avval ularni boshqa toifaga o‘tkazing.',
  },

  media: {
    title: 'Rasmlar',
    subtitle: 'Yangiliklar va xizmatlar uchun rasmlar',
    upload: 'Rasm yuklash',
    uploading: 'Yuklanmoqda…',
    dropHere: 'Rasmni shu yerga tashlang',
    dropHint: 'yoki bosib tanlang · JPEG, PNG, WebP · 10 MB gacha',
    dropHintVideo: 'yoki bosib tanlang · JPEG, PNG, WebP yoki MP4 · 10 MB gacha',
    video: 'Video',
    emptyTitle: "Hali rasm yo'q",
    emptyHint: 'Yangiliklarga muqova qo‘yish uchun avval rasm yuklang.',
    emptyAction: 'Birinchi rasmni yuklang',
    filterAll: 'Hammasi',
    filterUnused: 'Ishlatilmayotgan',
    pick: 'Tanlash',
    inUse: 'ishlatilmoqda',
    unused: 'ishlatilmayapti',
    usedBy: 'Bu yerlarda ishlatilyapti',
    deleteInUse: 'Bu rasm ishlatilmoqda. Avval uni almashtiring.',
    tooLarge: (mb: number) => `Rasm juda katta (${mb} MB). 10 MB gacha bo'lishi kerak.`,
    wrongType: 'Bu fayl turi qabul qilinmaydi. JPEG, PNG yoki WebP yuklang.',
    uploadFailed: 'Yuklanmadi',
    dimensions: 'o‘lchami',
  },

  redirects: {
    title: "Yo'naltirishlar",
    subtitle: 'Eski manzillardan yangisiga o‘tkazish',
    new: "Yo'naltirish qo'shish",
    from: 'Eski manzil',
    to: 'Yangi manzil',
    fromHint: 'Masalan /uz/news/eski-nom',
    code: 'Turi',
    code301: '301 — butunlay ko‘chirildi',
    code302: '302 — vaqtincha',
    code308: '308 — butunlay (usul saqlanadi)',
    active: 'Ishlayapti',
    turnOn: 'Yo‘naltirishni yoqish',
    turnOff: 'Yo‘naltirishni o‘chirish',
    automatic: 'avtomatik',
    manual: 'qo‘lda',
    emptyTitle: "Yo'naltirishlar yo'q",
    emptyHint:
      'Sahifa manzilini o‘zgartirsangiz, yo‘naltirish avtomatik yaratiladi. Bu yerda qo‘lda ham qo‘shish mumkin.',
    emptyAction: "Yo'naltirish qo'shing",
    selfRedirect: 'Manzil o‘zini o‘ziga yo‘naltira olmaydi.',
  },

  scans: {
    // Qidiruv rejimi (konsolning asosiy ekrani)
    consoleTitle: 'RS Telemetriya',
    consoleKicker: 'Skanerlash nazorati',
    backToCms: 'CMS ga qaytish',
    searchHeading: 'Qaysi raqamni tekshiramiz?',
    searchLead: "Quti yoki trek raqamini kiriting — u qayerda, kim tomonidan va qanday usulda skanerlangani ko'rinadi.",
    searchAction: 'Topish',
    browseAction: "Butun ro'yxatni ko'rish",
    newSearch: 'Yangi qidiruv',
    resultsFor: 'Natija',
    scanCount: 'ta skan',
    firstScan: 'Birinchi skan',
    lastScan: 'Oxirgi skan',
    operators: 'Xodimlar',
    timeline: 'Skanerlash tarixi',
    notFoundTitle: 'Bu raqam bo\'yicha skan yo\'q',
    notFoundHint: "Raqamni tekshirib qaytadan urinib ko'ring yoki butun ro'yxatdan qidiring.",
    searching: 'Qidirilmoqda…',
    todayScans: 'Bugun',
    liveLabel: 'Jonli',
    filters: 'Filtrlar',
    table: 'Jadval',
    map: 'Xarita',
    showing: 'ko\'rsatilmoqda',
    of: 'dan',
    title: 'RS skanerlar',
    subtitle: "Qayerda, qanday usulda va qachon skanerlangani — nazorat jurnali",
    clearFilters: 'Filtrlarni tozalash',
    export: 'CSV yuklab olish',
    searchPlaceholder: 'Quti yoki trek raqami',
    allOperators: 'Barcha xodimlar',
    allKinds: 'Barcha amallar',
    allSources: 'Barcha usullar',
    range: { today: 'Bugun', week: '7 kun', month: '30 kun', all: 'Hammasi' },
    sourcePda: 'PDA lazer',
    sourceManual: "Qo'lda",
    sourceCamera: 'Kamera',
    kindReceive: 'RS qabul',
    kindSorting: 'Saralash',
    kindSticker: 'Stiker',
    resultOk: 'Muvaffaqiyatli',
    resultFailed: 'Xato',
    suspicious: 'Shubhali',
    statTotal: 'Jami skan',
    statOk: 'Muvaffaqiyatli',
    statFailed: 'Xato',
    statLocated: 'Joylashuv bilan',
    statSuspicious: 'Shubhali',
    withoutLocation: 'joylashuvsiz',
    mapTitle: 'Skanerlash joylari',
    mapHint: "Yaqinlashtirish uchun Ctrl + g'ildirak yoki +/− tugmalari. Nuqtaga bosing — batafsil ma'lumot.",
    gridMode: "Katak rejimi",
    mockedGps: 'Soxta GPS',
    noFix: 'Yo\u2018q',
    noFixHint: "Skan paytida GPS signali bo'lmagan. Yopiq omborda bu odatiy hol — skanerlash bloklanmaydi.",
    emptyTitle: 'Skanlar topilmadi',
    emptyHint: "Tanlangan davr yoki filtrlarga mos skan yo'q. Filtrlarni tozalab ko'ring.",
    prev: 'Oldingi',
    next: 'Keyingi',
    operator: 'Xodim',
    role: 'Lavozim',
    location: 'Joylashuv',
    accuracy: 'Aniqlik',
    openInMap: 'Xaritada ochish',
    time: 'Vaqt',
    serverTime: 'Server vaqti',
    deviceTime: 'Qurilma vaqti',
    clockSkew: "Soat farqi",
    minutes: 'daqiqa',
    duration: 'Javob vaqti',
    device: 'Qurilma',
    serverError: 'Server xabari',
  },

  leads: {
    title: 'Murojaatlar',
    subtitle: 'Saytdagi shakl orqali kelgan so‘rovlar',
    emptyTitle: "Hozircha murojaat yo'q",
    emptyHint: 'Saytdagi aloqa shakli to‘ldirilganda so‘rovlar shu yerda paydo bo‘ladi.',
    name: 'Ism',
    contact: 'Telefon',
    message: 'Xabar',
    source: 'Qayerdan',
    received: 'Kelgan vaqti',
    markRead: "O'qilgan deb belgilash",
    markUnread: "O'qilmagan deb belgilash",
    export: 'CSV yuklab olish',
    statusNew: 'Yangi',
    statusContacted: "Bog'lanildi",
    statusQualified: 'Mos keladi',
    statusClosed: 'Yopildi',
    statusSpam: 'Spam',
    campaign: 'Kampaniya',
    landingPage: 'Qaysi sahifadan',
  },

  users: {
    title: 'Foydalanuvchilar',
    subtitle: 'Kim tizimga kira oladi',
    new: "Foydalanuvchi qo'shish",
    name: 'Ism',
    username: 'Login',
    usernameHint: 'Kirish uchun login — lotin harf, raqam, . _ - (masalan: dilshod yoki smm.manager).',
    email: 'Email',
    emailHint: 'Ixtiyoriy. Login o‘rniga email bilan ham kirish mumkin.',
    password: 'Parol',
    passwordHint: 'Kamida 10 belgi. Foydalanuvchi shu login va parol bilan kiradi.',
    role: 'Rol',
    roleHint: 'Rol foydalanuvchi qaysi sahifalarga kira olishini belgilaydi.',
    noRole: 'Rol tanlanmagan',
    lastLogin: 'Oxirgi kirish',
    neverLoggedIn: 'hali kirmagan',
    active: 'Faol',
    disabled: 'O‘chirilgan',
    resetPassword: 'Yangi parol berish',
    resetPasswordBody:
      'Yangi parol bir marta ko‘rsatiladi. Uni foydalanuvchiga o‘zingiz yetkazing — keyin qayta ko‘rish imkoni bo‘lmaydi.',
    issuedPassword: 'Yangi parol',
    issuedPasswordHint:
      'Nusxa oling va foydalanuvchiga bering. Kirganda undan parolni o‘zgartirish so‘raladi.',
    disable: 'Faolsizlantirish',
    enable: 'Yoqish',
    delete: 'O‘chirish',
    deleteBody:
      'Bu foydalanuvchi butunlay o‘chiriladi va qayta kira olmaydi. Bu amalni qaytarib bo‘lmaydi.',
    deleteSelf: 'O‘z hisobingizni o‘chira olmaysiz',
    lastAdmin: 'Bu tizimdagi yagona administrator.',
  },

  roles: {
    title: 'Rollar',
    subtitle: 'Har bir rol qaysi sahifalarga kira olishini belgilang',
    new: 'Rol yaratish',
    edit: 'Rolni tahrirlash',
    name: 'Rol nomi',
    nameHint: 'Masalan: SMM menejer, Kontent muharriri, Ombor nazoratchisi.',
    description: 'Izoh',
    descriptionHint: 'Ixtiyoriy — bu rol kimlar uchunligini qisqa yozing.',
    permissions: 'Ruxsat etilgan sahifalar',
    permissionsHint: 'Foydalanuvchi faqat belgilangan sahifalarni ko‘radi va ocha oladi.',
    permissionsCount: (n: number) => `${n} ta sahifa`,
    users: 'Foydalanuvchilar',
    usersCount: (n: number) => `${n} ta foydalanuvchi`,
    system: 'Tizim roli',
    superAdmin: 'Bosh administrator',
    superAdminNote: 'Bosh administrator barcha sahifalarga kira oladi — o‘zgartirib bo‘lmaydi.',
    delete: 'Rolni o‘chirish',
    deleteBody: 'Bu rol butunlay o‘chiriladi. Bu amalni qaytarib bo‘lmaydi.',
    deleteHasUsers: 'Bu rolda foydalanuvchilar bor. Avval ularni boshqa rolga o‘tkazing.',
    deleteSystem: 'Tizim rolini o‘chirib bo‘lmaydi.',
    empty: 'Hali rollar yo‘q',
    selectAll: 'Hammasini tanlash',
    clearAll: 'Tozalash',
  },

  pageLabels: {
    stories: 'Hikoyalar',
    media: 'Rasmlar',
    pages: 'Sahifalar',
    articles: 'Yangiliklar',
    services: 'Xizmatlar',
    categories: 'Toifalar',
    redirects: "Yo'naltirishlar",
    leads: 'Murojaatlar',
    scans: 'RS skanerlar',
    audit: "O'zgarishlar tarixi",
    users: 'Foydalanuvchilar va rollar',
  } as Record<string, string>,

  audit: {
    title: "O'zgarishlar tarixi",
    subtitle: 'Kim nimani qachon o‘zgartirdi',
    who: 'Kim',
    what: 'Nima',
    when: 'Qachon',
    field: 'Maydon',
    from: 'Edi',
    to: 'Bo‘ldi',
    empty: 'Hali o‘zgarishlar yo‘q',
    actions: {
      'article.create': 'yangilik qo‘shdi',
      'article.update': 'yangilikni o‘zgartirdi',
      'article.publish': 'yangilikni nashr etdi',
      'article.unpublish': 'yangilikni saytdan oldi',
      'article.delete': 'yangilikni o‘chirdi',
      'article.restore': 'yangilikni qaytardi',
      'service.create': 'xizmat qo‘shdi',
      'service.update': 'xizmatni o‘zgartirdi',
      'service.publish': 'xizmatni nashr etdi',
      'service.unpublish': 'xizmatni saytdan oldi',
      'service.delete': 'xizmatni o‘chirdi',
      'category.create': 'toifa qo‘shdi',
      'category.update': 'toifani o‘zgartirdi',
      'category.delete': 'toifani o‘chirdi',
      'category.reorder': 'toifalar tartibini o‘zgartirdi',
      'page.update': 'sahifani o‘zgartirdi',
      'media.upload': 'rasm yukladi',
      'media.delete': 'rasmni o‘chirdi',
      'media.alt': 'rasm tavsifini o‘zgartirdi',
      'redirect.create': 'yo‘naltirish qo‘shdi',
      'redirect.update': 'yo‘naltirishni o‘zgartirdi',
      'redirect.delete': 'yo‘naltirishni o‘chirdi',
      'user.create': 'foydalanuvchi qo‘shdi',
      'user.update': 'foydalanuvchini o‘zgartirdi',
      'user.role_change': 'huquqni o‘zgartirdi',
      'user.reset_password': 'parolni yangiladi',
      'user.deactivate': 'foydalanuvchini faolsizlantirdi',
      'user.delete': 'foydalanuvchini o‘chirdi',
      'role.create': 'rol yaratdi',
      'role.update': 'rolni o‘zgartirdi',
      'role.delete': 'rolni o‘chirdi',
      'auth.login': 'tizimga kirdi',
      'auth.change_password': 'parolini o‘zgartirdi',
      'lead.update': 'murojaatni o‘zgartirdi',
      'lead.delete': 'murojaatni o‘chirdi',
      'lead.export': 'murojaatlarni yuklab oldi',
    } as Record<string, string>,
  },

  palette: {
    placeholder: 'Qidiring yoki amal tanlang…',
    noResults: 'Hech narsa topilmadi',
    sections: { go: 'O‘tish', create: 'Yaratish', actions: 'Amallar' },
    toggleTheme: 'Mavzuni almashtirish',
    switchLang: 'Interfeys tilini almashtirish',
  },

  errors: {
    network: 'Serverga ulanib bo‘lmadi. Internetni tekshiring va qayta urinib ko‘ring.',
    unknown: 'Kutilmagan xatolik yuz berdi.',
    notFound: 'Bunday sahifa yo‘q',
    notFoundHint: 'Manzil noto‘g‘ri bo‘lishi mumkin.',
    goHome: 'Boshqaruv paneliga qaytish',
    forbidden: 'Bu bo‘limga ruxsatingiz yo‘q',
    forbiddenHint: 'Kerak bo‘lsa administratordan so‘rang.',
  },

  theme: {
    light: 'Yorug‘',
    dark: 'Qorong‘i',
    system: 'Tizim bo‘yicha',
    switchToLight: 'Yorug‘ mavzuga o‘tish',
    switchToDark: 'Qorong‘i mavzuga o‘tish',
  },
};

/**
 * Deliberately NOT `as const`: the Russian file is typed against this shape,
 * and literal types would require every Russian string to equal its Uzbek
 * original. What must match is the set of keys, which this gives us.
 */
export type Dictionary = typeof uz;
